import { pinyin } from 'pinyin-pro';
import { TERMINAL_TYPE } from "../api/enums.js";
import http from '../api/httpRequest.js';

let friendById = new Map();
let friendIndexById = new Map();
let friendsList = [];
let onlineById = new Map();
let onlineFlushTimer = null;
let onlineFlushMaxTimer = null;
let onlineDirty = false;
let onlineFlushStore = null;
const letterCache = new Map();
const OFFLINE_STATE = Object.freeze({ onlineWeb: false, onlineApp: false, online: false });
const ONLINE_WEB_STATE = Object.freeze({ onlineWeb: true, onlineApp: false, online: true });
const ONLINE_APP_STATE = Object.freeze({ onlineWeb: false, onlineApp: true, online: true });
const ONLINE_BOTH_STATE = Object.freeze({ onlineWeb: true, onlineApp: true, online: true });

function computeFriendLetter(name) {
	const text = (name || '').trim();
	if (!text) {
		return '#';
	}
	if (letterCache.has(text)) {
		return letterCache.get(text);
	}
	const first = text[0];
	let letter = '#';
	if (/[A-Za-z]/.test(first)) {
		letter = first.toUpperCase();
	} else {
		try {
			const py = pinyin(first, { toneType: 'none', pattern: 'first' });
			const ch = (typeof py === 'string' ? py[0] : '') || '';
			letter = /[A-Za-z]/.test(ch) ? ch.toUpperCase() : '#';
		} catch (e) {
			letter = '#';
		}
	}
	letterCache.set(text, letter);
	return letter;
}

function friendLookupKey(id) {
	if (id == null || id === '') {
		return null;
	}
	return id;
}

function getFriendById(id) {
	const key = friendLookupKey(id);
	if (key == null) {
		return;
	}
	return friendById.get(key) || friendById.get(Number(key)) || friendById.get(String(key));
}

function packOnlineState(onlineWeb, onlineApp) {
	if (onlineWeb && onlineApp) {
		return ONLINE_BOTH_STATE;
	}
	if (onlineWeb) {
		return ONLINE_WEB_STATE;
	}
	if (onlineApp) {
		return ONLINE_APP_STATE;
	}
	return OFFLINE_STATE;
}

function setOnlineEntry(id, onlineState) {
	if (id == null || id === '') {
		return;
	}
	onlineById.set(id, onlineState);
	onlineById.set(String(id), onlineState);
	onlineById.set(Number(id), onlineState);
}

function getOnlineState(id) {
	if (id == null || id === '') {
		return OFFLINE_STATE;
	}
	const overlay = onlineById.get(id) || onlineById.get(Number(id)) || onlineById.get(String(id));
	if (overlay) {
		return overlay;
	}
	const friend = getFriendById(id);
	if (!friend) {
		return OFFLINE_STATE;
	}
	return packOnlineState(!!friend.onlineWeb, !!friend.onlineApp);
}

export function peekFriendOnline(id) {
	return getOnlineState(id);
}

function seedOnlineFromFriend(friend) {
	if (!friend || friend.id == null) {
		return;
	}
	setOnlineEntry(friend.id, packOnlineState(!!friend.onlineWeb, !!friend.onlineApp));
}

function clearOnlineFlushTimers() {
	if (onlineFlushTimer) {
		clearTimeout(onlineFlushTimer);
		onlineFlushTimer = null;
	}
	if (onlineFlushMaxTimer) {
		clearTimeout(onlineFlushMaxTimer);
		onlineFlushMaxTimer = null;
	}
	onlineDirty = false;
	onlineFlushStore = null;
}

function flushOnlineStatusNow() {
	if (onlineFlushTimer) {
		clearTimeout(onlineFlushTimer);
		onlineFlushTimer = null;
	}
	if (onlineFlushMaxTimer) {
		clearTimeout(onlineFlushMaxTimer);
		onlineFlushMaxTimer = null;
	}
	const store = onlineFlushStore;
	const dirty = onlineDirty;
	onlineFlushStore = null;
	onlineDirty = false;
	if (dirty && store) {
		store.commit('flushOnlineStatus');
	}
}

function scheduleOnlineFlush(store) {
	onlineDirty = true;
	onlineFlushStore = store;
	if (onlineFlushTimer) {
		clearTimeout(onlineFlushTimer);
	}
	onlineFlushTimer = setTimeout(flushOnlineStatusNow, 120);
	if (!onlineFlushMaxTimer) {
		onlineFlushMaxTimer = setTimeout(flushOnlineStatusNow, 320);
	}
}

function setFriendMapEntry(friend) {
	if (!friend || friend.id == null) {
		return;
	}
	friendById.set(friend.id, friend);
	friendById.set(String(friend.id), friend);
	friendById.set(Number(friend.id), friend);
}

function rebuildFriendIndex(list) {
	friendIndexById = new Map();
	for (let i = 0; i < list.length; i++) {
		const id = list[i] && list[i].id;
		if (id == null) {
			continue;
		}
		friendIndexById.set(id, i);
		friendIndexById.set(String(id), i);
		friendIndexById.set(Number(id), i);
	}
}

function freezeFriend(raw, prev) {
	const showNickName = raw.showNickName || (prev && prev.showNickName) || raw.nickName || '';
	const merged = Object.assign({}, prev || {}, raw, {
		onlineWeb: raw.onlineWeb != null ? !!raw.onlineWeb : !!(prev && prev.onlineWeb),
		onlineApp: raw.onlineApp != null ? !!raw.onlineApp : !!(prev && prev.onlineApp),
		deleted: raw.deleted != null ? !!raw.deleted : !!(prev && prev.deleted),
		phone: raw.phone != null && raw.phone !== '' ? raw.phone : ((prev && prev.phone) || ''),
		showNickName,
		letter: (prev && prev.letter && prev.showNickName === showNickName)
			? prev.letter
			: computeFriendLetter(showNickName)
	});
	merged.online = !!(merged.onlineWeb || merged.onlineApp || raw.online);
	return Object.freeze(merged);
}

function syncFriendsToState(state) {
	state.friends = Object.freeze(friendsList.slice());
}

function replaceFriendAt(state, next, options) {
	const frozen = Object.isFrozen(next) ? next : freezeFriend(next);
	setFriendMapEntry(frozen);
	const idx = friendIndexById.get(frozen.id);
	if (idx != null && friendsList[idx] && friendsList[idx].id == frozen.id) {
		friendsList[idx] = frozen;
	} else {
		const found = friendsList.findIndex((f) => f.id == frozen.id);
		if (found >= 0) {
			friendsList[found] = frozen;
			rebuildFriendIndex(friendsList);
		} else {
			friendsList.unshift(frozen);
			rebuildFriendIndex(friendsList);
		}
	}
	if (!options || !options.skipClone) {
		syncFriendsToState(state);
	}
	state.friendsVersion++;
}

function resetFriendIndex() {
	friendById = new Map();
	friendIndexById = new Map();
	onlineById = new Map();
}

export default {

	state: {
		friends: [], // 好友列表
		friendsVersion: 0,
		onlineVersion: 0,
		requests: [], // 好友申请列表
		timer: null
	},
	mutations: {
		setFriends(state, friends) {
			const source = Array.isArray(friends) ? friends : [];
			resetFriendIndex();
			const list = new Array(source.length);
			let size = 0;
			for (let i = 0; i < source.length; i++) {
				const raw = source[i];
				if (!raw || raw.id == null) {
					continue;
				}
				const frozen = freezeFriend(raw);
				setFriendMapEntry(frozen);
				seedOnlineFromFriend(frozen);
				list[size++] = frozen;
			}
			list.length = size;
			friendsList = list;
			rebuildFriendIndex(list);
			syncFriendsToState(state);
			state.friendsVersion++;
			this.commit("syncChatUserTagFromFriends");
		},
		setRequests(state, requests) {
			state.requests = Array.isArray(requests) ? requests : [];
		},
		addRequest(state, request) {
			state.requests.unshift(request);
		},
		removeRequest(state, id) {
			for (let idx in state.requests) {
				if (state.requests[idx].id == id) {
					state.requests.splice(idx, 1);
					break;
				}
			}
		},
		updateFriend(state, friend) {
			if (!friend || friend.id == null) {
				return;
			}
			const prev = getFriendById(friend.id);
			if (!prev) {
				return;
			}
			const live = getOnlineState(friend.id);
			const next = freezeFriend(Object.assign({}, friend, {
				onlineWeb: live.onlineWeb,
				onlineApp: live.onlineApp
			}), prev);
			replaceFriendAt(state, next);
			seedOnlineFromFriend(next);
			this.commit("updateChatFromFriend", next);
		},
		removeFriend(state, id) {
			const prev = getFriendById(id);
			if (prev) {
				replaceFriendAt(state, freezeFriend({ deleted: true }, prev));
			}
			this.commit("removePrivateChat", id);
		},
		addFriend(state, friend) {
			if (!friend || friend.id == null) {
				return;
			}
			const next = Object.assign({}, friend, { deleted: false });
			if (getFriendById(friend.id)) {
				this.commit("updateFriend", next)
			} else {
				const frozen = freezeFriend(next);
				replaceFriendAt(state, frozen);
				seedOnlineFromFriend(frozen);
				this.commit("updateChatFromFriend", frozen);
			}
			this.commit("unhidePrivateChat", friend.id);
		},
		updateOnlineStatus(state, onlineData) {
			if (!onlineData || onlineData.userId == null) {
				return;
			}
			if (!getFriendById(onlineData.userId)) {
				return;
			}
			const prev = getOnlineState(onlineData.userId);
			let onlineWeb = prev.onlineWeb;
			let onlineApp = prev.onlineApp;
			if (onlineData.terminal == TERMINAL_TYPE.WEB) {
				onlineWeb = !!onlineData.online;
			} else if (onlineData.terminal == TERMINAL_TYPE.APP) {
				onlineApp = !!onlineData.online;
			} else {
				return;
			}
			const next = packOnlineState(onlineWeb, onlineApp);
			if (next === prev) {
				return;
			}
			setOnlineEntry(onlineData.userId, next);
			scheduleOnlineFlush(this);
		},
		flushOnlineStatus(state) {
			state.onlineVersion++;
		},
		clear(state) {
			state.timer && clearTimeout(state.timer);
			clearOnlineFlushTimers();
			resetFriendIndex();
			friendsList = [];
			state.friends = Object.freeze([]);
			state.friendsVersion++;
			state.onlineVersion++;
			state.timer = null;
		}
	},
	actions: {
		loadFriend(context) {
			return new Promise((resolve, reject) => {
				http({
					url: '/friend/list',
					method: 'GET'
				}).then(async (friends) => {
					context.commit("setFriends", friends);
					// 加载好友申请列表
					const requests = await http({
						url: '/friend/request/list',
						method: 'GET'
					});
					context.commit("setRequests", requests);
					resolve();
				}).catch((res) => {
					reject();
				})
			});
		}
	},
	getters: {
		friendList: (state) => state.friends,
		friendOnline: (state) => {
			const version = state.onlineVersion;
			return (userId) => {
				if (version < 0) {
					return OFFLINE_STATE;
				}
				return getOnlineState(userId);
			};
		},
		isFriend: (state) => {
			const version = state.friendsVersion;
			return (userId) => {
				if (version < 0) {
					return false;
				}
				const friend = getFriendById(userId);
				return !!(friend && !friend.deleted);
			};
		},
		findFriend: (state) => {
			const version = state.friendsVersion;
			return (userId) => {
				if (version < 0) {
					return;
				}
				return getFriendById(userId);
			};
		},
		isInRecvRequest: (state) => (userId) => {
			if (!Array.isArray(state.requests)) {
				return false;
			}
			return state.requests.some((req) => req.recvId == userId);
		},
		isInSendRequest: (state) => (userId) => {
			if (!Array.isArray(state.requests)) {
				return false;
			}
			return state.requests.some((req) => req.sendId == userId);
		}
	}

}
